/**
 * Se dare retta o no alla preferenza di sistema «movimento ridotto».
 *
 * Acceso (default), il sito **ignora** `prefers-reduced-motion` e le
 * animazioni girano per tutti. Spento (`NEXT_PUBLIC_IGNORE_REDUCED_MOTION=0`),
 * si torna a rispettarlo: ogni animazione salta al fotogramma finale.
 *
 * È una scelta di prodotto, presa sapendo cosa comporta: quel settaggio lo
 * accende anche chi ha disturbi vestibolari, emicrania o cinetosi, e la
 * sezione «Dal caso al documento» blocca la pagina e muove il contenuto sotto
 * lo scorrimento — che è proprio il movimento che dà sintomi. Ignorarlo
 * significa mostrarlo lo stesso a chi aveva chiesto di non vederlo. Sta qui,
 * dietro a un interruttore e non sparso per il codice, perché il giorno che
 * si cambia idea si cambia una riga.
 *
 * Da qui passano tutti: i rami di `gsap.matchMedia()`, le letture dirette
 * della media query, e — tramite `data-motion` sull'`html` — anche le regole
 * CSS di `globals.css`, che altrimenti resterebbero a spegnere le animazioni
 * per conto loro.
 */
export const IGNORE_REDUCED_MOTION = process.env.NEXT_PUBLIC_IGNORE_REDUCED_MOTION !== '0';

/**
 * La condizione del ramo animato di `gsap.matchMedia()`.
 *
 * `all` corrisponde sempre, quindi il ramo gira comunque; senza la deroga
 * resta la media query vera.
 */
export const MOTION_OK = IGNORE_REDUCED_MOTION ? 'all' : '(prefers-reduced-motion: no-preference)';

/**
 * La condizione del ramo a movimento ridotto, quello che disegna il
 * fotogramma finale senza animare. `not all` non corrisponde mai, quindi con
 * la deroga attiva non viene costruito.
 */
export const MOTION_REDUCED = IGNORE_REDUCED_MOTION
  ? 'not all'
  : '(prefers-reduced-motion: reduce)';

/**
 * Il segno lasciato sull'`html` quando la preferenza va ignorata: è quello che
 * disinnesca i blocchi `@media (prefers-reduced-motion: reduce)` del CSS, che
 * non possono leggere una variabile d'ambiente.
 */
export const MOTION_ATTRIBUTE = IGNORE_REDUCED_MOTION ? 'always' : undefined;

/**
 * `true` se l'utente ha chiesto movimento ridotto **e** noi lo stiamo
 * rispettando. Con la deroga attiva risponde sempre `false`, così chi la usa
 * per saltare un'animazione non la salta più.
 */
export function prefersReducedMotion(): boolean {
  if (IGNORE_REDUCED_MOTION) return false;
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
